import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Checkbox, SelectInput, TextInput } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption, LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import type { HeroArticleSuggestion } from "@/lib/material";
import {
  isHeroMaterialOption,
  manualMaterialSuggestion,
  manualMaterialToOption,
  materialRecordToOption,
  timesheetMaterialDisplayName,
  timesheetMaterialSource,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";
import {
  listStuecklisteTkNummern,
  STUECKLISTE_POSITION_STATUS_OPTIONS,
  STUECKLISTE_SCHNITTLAGE_OPTIONS,
  type StuecklistePosition,
  type StuecklistePositionStatus,
  type StuecklisteSchnittlage,
  type StuecklisteTkNummer,
} from "@/lib/stuecklisten";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

interface PositionFormState {
  beschreibung: string;
  anzahl: string;
  groesse: string;
  bemerkung: string;
  artikel_id: number | null;
  hero_nr: string;
  source_type: string;
  position_status: StuecklistePositionStatus;
  order_line_id: number | null;
  bestellnummer: string;
  zuschnitt_erforderlich: boolean;
  ist_montagematerial: boolean;
  schnittwinkel_links: string;
  schnittwinkel_rechts: string;
  schnittlage: StuecklisteSchnittlage;
}

function parseWinkel(value: string, fallback: number | null = 90): number | null {
  const raw = value.trim();
  if (!raw) return fallback;
  const parsed = parseFloat(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function isValidSchnittwinkel(value: number | null): value is number {
  return value != null && Number.isFinite(value) && value > 0 && value < 180;
}

function positionToForm(pos: StuecklistePosition | null | undefined): PositionFormState {
  if (!pos) {
    return {
      beschreibung: "",
      anzahl: "1",
      groesse: "",
      bemerkung: "",
      artikel_id: null,
      hero_nr: "",
      source_type: "manual",
      position_status: "offen",
      order_line_id: null,
      bestellnummer: "",
      zuschnitt_erforderlich: false,
      ist_montagematerial: false,
      schnittwinkel_links: "90",
      schnittwinkel_rechts: "90",
      schnittlage: "gegenlaeufig",
    };
  }
  return {
    beschreibung: pos.beschreibung || "",
    anzahl: pos.anzahl != null ? String(pos.anzahl) : "",
    groesse: pos.groesse || "",
    bemerkung: pos.bemerkung || "",
    artikel_id: pos.artikel_id ?? null,
    hero_nr: pos.hero_nr || "",
    source_type: pos.source_type || "manual",
    position_status: (pos.position_status || "offen") as StuecklistePositionStatus,
    order_line_id: pos.order_line_id ?? null,
    bestellnummer: pos.bestellnummer || "",
    zuschnitt_erforderlich: Boolean(pos.zuschnitt_erforderlich),
    ist_montagematerial: Boolean(pos.ist_montagematerial),
    schnittwinkel_links:
      pos.schnittwinkel_links != null ? String(pos.schnittwinkel_links) : "90",
    schnittwinkel_rechts:
      pos.schnittwinkel_rechts != null ? String(pos.schnittwinkel_rechts) : "90",
    schnittlage: (pos.schnittlage as StuecklisteSchnittlage) || "gegenlaeufig",
  };
}

function formToPosition(
  form: PositionFormState,
  existing?: StuecklistePosition | null,
): StuecklistePosition {
  const anzahlRaw = form.anzahl.trim();
  const anzahl = anzahlRaw ? parseFloat(anzahlRaw.replace(",", ".")) || 0 : null;
  const isBestellt = form.position_status === "bestellt";
  const zuschnitt = form.zuschnitt_erforderlich;
  const left = zuschnitt ? parseWinkel(form.schnittwinkel_links) : null;
  const right = zuschnitt ? parseWinkel(form.schnittwinkel_rechts) : null;
  const needsLage = zuschnitt && ((left ?? 90) !== 90 || (right ?? 90) !== 90);
  return {
    ...(existing ?? {}),
    position_nr: existing?.position_nr || 1,
    position_kind: "standard",
    beschreibung: form.beschreibung.trim() || null,
    anzahl,
    groesse: form.groesse.trim() || null,
    bemerkung: form.bemerkung.trim() || null,
    artikel_id: form.artikel_id,
    hero_nr: form.hero_nr.trim() || null,
    source_type: form.source_type || "manual",
    position_status: form.position_status || "offen",
    order_line_id: isBestellt ? form.order_line_id : null,
    bestellnummer: isBestellt ? form.bestellnummer.trim() || null : null,
    zuschnitt_erforderlich: zuschnitt,
    ist_montagematerial: form.ist_montagematerial,
    schnittwinkel_links: left,
    schnittwinkel_rechts: right,
    schnittlage: needsLage ? form.schnittlage || "gegenlaeufig" : null,
  };
}

function applyHeroArticle(form: PositionFormState, article: HeroArticleSuggestion): PositionFormState {
  const label = timesheetMaterialDisplayName(article);
  if (article.artikel_id) {
    return {
      ...form,
      beschreibung: label,
      artikel_id: article.artikel_id,
      hero_nr: article.hero_nr || "",
      source_type: form.order_line_id ? "order_line" : "hero",
    };
  }
  return {
    ...form,
    beschreibung: label,
    artikel_id: null,
    hero_nr: "",
    source_type: form.order_line_id ? "order_line" : "manual",
  };
}

export function StuecklistePositionDialog({
  open,
  orderId,
  editPosition,
  onClose,
  onSave,
}: {
  open: boolean;
  orderId: number;
  editPosition: StuecklistePosition | null;
  onClose: () => void;
  onSave: (position: StuecklistePosition, mode: "create" | "edit") => void;
}) {
  const mode = editPosition ? "edit" : "create";
  const [form, setForm] = useState<PositionFormState>(() => positionToForm(editPosition));
  const [baseline, setBaseline] = useState<PositionFormState>(() => positionToForm(editPosition));
  const [materialOption, setMaterialOption] = useState<AutocompleteOption<HeroArticleSuggestion> | null>(
    null,
  );
  const [tkNummern, setTkNummern] = useState<StuecklisteTkNummer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const materialAcRef = useRef<LockedAutocompleteHandle>(null);

  useEffect(() => {
    if (!open) return;
    const next = positionToForm(editPosition);
    setForm(next);
    setBaseline(next);
    setMaterialOption(
      materialRecordToOption({
        text: next.beschreibung,
        artikel_id: next.artikel_id,
        hero_nr: next.hero_nr,
      }),
    );
    setError(null);
  }, [open, editPosition]);

  useEffect(() => {
    if (!open || form.position_status !== "bestellt") return;
    let cancelled = false;
    void listStuecklisteTkNummern(orderId)
      .then((list) => {
        if (!cancelled) setTkNummern(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setTkNummern([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderId, form.position_status]);

  const dirty = useMemo(() => hasFormChanges(form, baseline), [form, baseline]);

  const selectedTk = useMemo(
    () => tkNummern.find((row) => row.bestellnummer === form.bestellnummer) || null,
    [tkNummern, form.bestellnummer],
  );

  function handleSave() {
    const pending = materialAcRef.current?.getPendingQuery()?.trim() ?? "";
    const label = materialOption?.label?.trim() || pending || form.beschreibung.trim();
    if (!label) {
      setError("Bitte einen Artikel auswählen oder eingeben.");
      return;
    }
    if (form.position_status === "bestellt" && !form.bestellnummer.trim()) {
      setError("Bitte eine TK-Nummer verknüpfen.");
      return;
    }
    if (form.zuschnitt_erforderlich) {
      const left = parseWinkel(form.schnittwinkel_links, null);
      const right = parseWinkel(form.schnittwinkel_rechts, null);
      if (!isValidSchnittwinkel(left) || !isValidSchnittwinkel(right)) {
        setError("Schnittwinkel müssen zwischen 0° und 180° liegen.");
        return;
      }
    }
    const nextForm =
      materialOption?.value?.artikel_id && materialOption.label.trim() === label
        ? { ...form, beschreibung: label }
        : applyHeroArticle({ ...form, beschreibung: label }, manualMaterialSuggestion(label));
    onSave(formToPosition(nextForm, editPosition), mode);
    onClose();
  }

  const showSchnittlage = useMemo(() => {
    if (!form.zuschnitt_erforderlich) return false;
    const left = parseWinkel(form.schnittwinkel_links) ?? 90;
    const right = parseWinkel(form.schnittwinkel_rechts) ?? 90;
    return left !== 90 || right !== 90;
  }, [form.zuschnitt_erforderlich, form.schnittwinkel_links, form.schnittwinkel_rechts]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Position bearbeiten" : "Position hinzufügen"}
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      dirty={dirty}
      footer={<PositionDialogFooter onClose={onClose} onSubmit={handleSave} />}
    >
      <div className="space-y-3">
        <LockedAutocomplete<HeroArticleSuggestion>
          ref={materialAcRef}
          label="Artikel / Beschreibung"
          placeholder="Artikel suchen oder Freitext …"
          value={materialOption}
          minChars={2}
          allowFreeText
          allowEditLocked={(opt) => !isHeroMaterialOption(opt)}
          createFreeTextOption={manualMaterialToOption}
          loadOptions={timesheetMaterialSource}
          onChange={(opt) => {
            setMaterialOption(opt);
            if (!opt) {
              setForm((prev) => ({
                ...prev,
                beschreibung: "",
                artikel_id: null,
                hero_nr: "",
                source_type: prev.order_line_id ? "order_line" : "manual",
              }));
              return;
            }
            setForm((prev) => applyHeroArticle(prev, opt.value));
          }}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextInput
            label="Anzahl"
            value={form.anzahl}
            onChange={(e) => setForm((prev) => ({ ...prev, anzahl: e.target.value }))}
          />
          <TextInput
            label="Größe"
            value={form.groesse}
            onChange={(e) => setForm((prev) => ({ ...prev, groesse: e.target.value }))}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <Checkbox
            label="Zuschnitt erforderlich"
            checked={form.zuschnitt_erforderlich}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                zuschnitt_erforderlich: e.target.checked,
                schnittwinkel_links: e.target.checked
                  ? prev.schnittwinkel_links || "90"
                  : "90",
                schnittwinkel_rechts: e.target.checked
                  ? prev.schnittwinkel_rechts || "90"
                  : "90",
                schnittlage: prev.schnittlage || "gegenlaeufig",
              }))
            }
          />
          <Checkbox
            label="Montagematerial (nur zurechtlegen)"
            checked={form.ist_montagematerial}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, ist_montagematerial: e.target.checked }))
            }
          />
        </div>

        {form.zuschnitt_erforderlich ? (
          <div className="space-y-3 rounded-md border border-separator bg-fill-quaternary p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextInput
                label="Schnittwinkel links (°)"
                value={form.schnittwinkel_links}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, schnittwinkel_links: e.target.value }))
                }
              />
              <TextInput
                label="Schnittwinkel rechts (°)"
                value={form.schnittwinkel_rechts}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, schnittwinkel_rechts: e.target.value }))
                }
              />
            </div>
            {showSchnittlage ? (
              <SelectInput
                label="Schnittlage"
                value={form.schnittlage}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    schnittlage: e.target.value as StuecklisteSchnittlage,
                  }))
                }
                hint="Gegenläufig = /\\ · Gleichläufig = //"
              >
                {STUECKLISTE_SCHNITTLAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectInput>
            ) : null}
          </div>
        ) : null}

        <SelectInput
          label="Status"
          value={form.position_status}
          onChange={(e) => {
            const next = e.target.value as StuecklistePositionStatus;
            setForm((prev) => ({
              ...prev,
              position_status: next,
              ...(next === "bestellt"
                ? {}
                : { order_line_id: null, bestellnummer: "", source_type: prev.artikel_id ? "hero" : "manual" }),
            }));
          }}
        >
          {STUECKLISTE_POSITION_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectInput>

        {form.position_status === "bestellt" ? (
          <div className="space-y-1">
            <SelectInput
              label="TK-Nummer"
              value={form.bestellnummer}
              onChange={(e) => {
                const bnr = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  bestellnummer: bnr,
                  order_line_id: null,
                  source_type: bnr ? "order_line" : prev.artikel_id ? "hero" : "manual",
                }));
              }}
            >
              <option value="">TK-Nummer wählen …</option>
              {tkNummern.map((row) => (
                <option key={row.bestellnummer} value={row.bestellnummer}>
                  {row.bestellnummer} · {row.tk_status_label}
                  {row.position_count ? ` · ${row.position_count} Pos.` : ""}
                </option>
              ))}
            </SelectInput>
            {selectedTk ? (
              <p className="text-xs text-label-secondary">
                TK-Status: <span className="font-medium text-label">{selectedTk.tk_status_label}</span>
              </p>
            ) : form.bestellnummer ? (
              <p className="text-xs text-label-tertiary">Verknüpfte TK: {form.bestellnummer}</p>
            ) : (
              <p className="text-xs text-label-tertiary">
                Keine TK-Bestellungen am Projekt? Positionen können auch aus den Vorschlägen übernommen werden.
              </p>
            )}
          </div>
        ) : null}

        <TextInput
          label="Bemerkung (optional)"
          value={form.bemerkung}
          onChange={(e) => setForm((prev) => ({ ...prev, bemerkung: e.target.value }))}
        />
        {form.hero_nr ? (
          <p className="text-xs text-label-tertiary">Hero-Nr.: {form.hero_nr}</p>
        ) : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function PositionDialogFooter({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button type="button" variant="secondary" size="sm" onClick={requestClose}>
        Abbrechen
      </Button>
      <Button type="button" size="sm" onClick={onSubmit}>
        Übernehmen
      </Button>
    </div>
  );
}
